import React from 'react';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import DurationStepper from 'components/CallScribe/DurationStepper';
import TimeEntryPickers from 'components/CallScribe/TimeEntryPickers';
import usePaperStyles from 'components/CallScribe/paperStyles';
import { parseUtcInstant } from 'components/CallScribe/Recents/recentsHelpers';
import { IUseTimeEntryOptionsResult } from 'components/CallScribe/useTimeEntryOptions';

const clock = (d: Date): string => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const meridiem = (label: string): string => label.replace(/^[\d:.\s]+/, '');

export const coveredRange = (
  endedAtUtc: string | null | undefined,
  minutes: number,
): string | null => {
  const end = parseUtcInstant(endedAtUtc);
  if (!end || minutes <= 0) return null;
  const startLabel = clock(new Date(end.getTime() - minutes * 60_000));
  const endLabel = clock(end);
  const suffix = meridiem(startLabel);
  const trimmedStart = suffix && suffix === meridiem(endLabel)
    ? startLabel.slice(0, startLabel.length - suffix.length).trim()
    : startLabel;
  return `Covers ${trimmedStart} – ${endLabel}`;
};

export interface ITimeEntrySectionProps {
  idBase: string;
  duration: number;
  onDurationChange: (minutes: number) => void;
  fields: IUseTimeEntryOptionsResult;
  endedAtUtc?: string | null;
  suggestedMinutes?: number | null;
  caveat?: string;
  children?: React.ReactNode;
}

const TimeEntrySection: React.FC<ITimeEntrySectionProps> = ({
  idBase,
  duration,
  onDurationChange,
  fields,
  endedAtUtc,
  suggestedMinutes,
  caveat,
  children,
}) => {
  const paper = usePaperStyles();
  const headingId = `${idBase}-time-entry`;
  const covers = coveredRange(endedAtUtc, duration);
  const helper = typeof suggestedMinutes === 'number' && suggestedMinutes > 0 && duration !== suggestedMinutes
    ? `Suggested ${suggestedMinutes} min`
    : undefined;

  return (
    <section className={paper.section} aria-labelledby={headingId}>
      <div className={paper.sectionHead}>
        <h3 id={headingId} className={paper.sectionLabel}>Time entry</h3>
        {covers ? <span className={paper.sectionAside}>{covers}</span> : null}
      </div>
      <div className={paper.fieldGrid}>
        <DurationStepper
          id={`${idBase}-duration`}
          value={duration}
          onChange={onDurationChange}
          helper={helper}
        />
        <TimeEntryPickers fields={fields} />
      </div>
      {!covers ? (
        <p className={paper.caption}>The entry covers the reviewed duration up to the end of the call.</p>
      ) : null}
      {caveat ? <p className={paper.caption}>{caveat}</p> : null}
      {fields.error ? (
        <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
          <p className={paper.noticeBody}>{fields.error}</p>
          <button type="button" className={paper.textButton} onClick={fields.reload}>
            <RefreshRoundedIcon aria-hidden />
            Try again
          </button>
        </div>
      ) : null}
      {children}
    </section>
  );
};

export default TimeEntrySection;
